/**
 * Dose Trial Buy Box: explainer dialog plus the optional add-to-cart gate
 * used for the ENG-1649 A/B test.
 *
 * The block renders a native <dialog> after the buy buttons. The "How does
 * this work?" link opens it in both variants; that link lives in the
 * separate description block higher in the buy box, so openers are looked
 * up across the enclosing section. When the host element carries
 * data-atc-opens-modal="true" (variant A) the product form's Start Trial
 * button opens the dialog instead of adding to cart, and the dialog's own
 * Start Trial submits the form. With the flag off (variant B) the form
 * submits as normal and the dialog's button still adds to cart.
 *
 * The gate shadows `handleSubmit` on the <product-form-component> instance.
 * Horizon's `on:submit` delegation (assets/component.js) resolves the method
 * on the instance, so an own property wins over the prototype method and the
 * original handler can be called through for the real add. Own properties
 * survive a later custom-element upgrade, so it is safe to install before the
 * product form's module has run.
 */
class DoseTrialBuybox extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  dialog = null;

  /** @type {HTMLElement | null} */
  lastOpener = null;

  #confirmed = false;

  connectedCallback() {
    this.dialog = this.querySelector('[data-dialog]');
    this.closeButton = this.querySelector('[data-close]');
    this.modalSubmit = this.querySelector('[data-modal-submit]');

    const scope = this.closest('.shopify-section') ?? document;
    scope.querySelectorAll('[data-open-modal]').forEach((button) => {
      button.addEventListener('click', this.#handleOpenClick);
    });
    this.closeButton?.addEventListener('click', this.#close);
    this.dialog?.addEventListener('click', this.#handleDialogClick);
    this.dialog?.addEventListener('close', this.#handleDialogClose);
    this.modalSubmit?.addEventListener('click', this.#submitFromModal);

    this.#installGate();
  }

  get gated() {
    return this.dataset.atcOpensModal === 'true';
  }

  /** @returns {HTMLFormElement | null} */
  get form() {
    return this.querySelector('form[data-type="add-to-cart-form"]');
  }

  #installGate() {
    const productForm = this.querySelector('product-form-component');
    if (!productForm) return;

    const host = this;

    // Resolve the original handler at call time, not install time: the
    // product form may not be upgraded yet, so its prototype method only
    // exists later.
    /** @type {any} */ (productForm).handleSubmit = function (/** @type {SubmitEvent} */ event) {
      if (host.gated && !host.#confirmed) {
        event.preventDefault();
        host.open(/** @type {HTMLElement | null} */ (event.submitter ?? null));
        return;
      }

      host.#confirmed = false;
      const original = Object.getPrototypeOf(this)?.handleSubmit;
      return typeof original === 'function' ? original.call(this, event) : undefined;
    };
  }

  /** @param {HTMLElement | null} [opener] */
  open(opener = null) {
    if (!this.dialog || this.dialog.open) return;

    this.lastOpener = opener;
    this.dialog.showModal();
    this.closeButton?.focus();
  }

  #handleOpenClick = (event) => {
    this.open(/** @type {HTMLElement} */ (event.currentTarget));
  };

  #close = () => {
    this.dialog?.close();
  };

  /** Clicking the ::backdrop (or the dialog's own padding) closes it — a click on any real content lands on a descendant, never on the dialog itself. */
  #handleDialogClick = (event) => {
    if (event.target === this.dialog) this.dialog?.close();
  };

  #handleDialogClose = () => {
    this.lastOpener?.focus();
    this.lastOpener = null;
  };

  #submitFromModal = () => {
    const form = this.form;
    if (!form) return;

    this.#confirmed = true;
    this.dialog?.close();
    form.requestSubmit();
  };
}

if (!customElements.get('dose-trial-buybox')) {
  customElements.define('dose-trial-buybox', DoseTrialBuybox);
}
